import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { useWhiteboard, WhiteboardOpenRunsError } from "../whiteboard/WhiteboardContext";
import type { WhiteboardOpenRuns, WhiteboardSnapshot } from "@calega/realtime";
import { ConfirmationModal } from "./ConfirmationModal";
import { useSoloWhiteboard } from "../whiteboard/SoloWhiteboardContext";

export function SessionPanel({ account }: { account: Account }) {
  const realtime = useRealtime();
  const whiteboard = useWhiteboard();
  const solo = useSoloWhiteboard();
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [openRuns, setOpenRuns] = useState<(WhiteboardOpenRuns & { sessionId: string; scope: Pick<WhiteboardSnapshot, "generation" | "permissionRevision"> }) | null>(null);
  const canLead = Boolean(account.actingAs) && account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const [, setClock] = useState(0);

  useEffect(() => {
    return realtime.restoreActive();
  }, [account.id]);

  useEffect(() => {
    if (!realtime.session) return;
    setClock(0);
    const timer = window.setInterval(() => setClock((value) => value + 1), 1_000);
    return () => window.clearInterval(timer);
  }, [realtime.session?.id]);

  const remainingSeconds = realtime.session
    ? Math.max(0, Math.ceil((new Date(realtime.session.expiresAt).getTime() - realtime.serverNow()) / 1_000))
    : null;
  const remainingText = remainingSeconds === null ? "" : `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, "0")} Minuten`;
  async function openWhiteboard(confirmed = openRuns) {
    if (!realtime.session || !whiteboard.snapshot) return;
    const sessionId = realtime.session.id;
    const scope = confirmed?.scope ?? { generation: whiteboard.snapshot.generation, permissionRevision: whiteboard.snapshot.permissionRevision };
    setMessage(""); setOpenRuns(null);
    try { await whiteboard.command({ type: "open", ...(confirmed ? { abortRunsConfirmation: confirmed.confirmation } : {}) }, scope); }
    catch (error) { if (error instanceof WhiteboardOpenRunsError) setOpenRuns({ ...error.openRuns, sessionId, scope }); }
  }

  if (realtime.session) {
    return (
      <section className="session-panel active">
        <div><span>Sitzung sync</span><strong>Code {realtime.session.code}</strong><small>{realtime.connected ? realtime.synchronized ? "Verbunden und synchron" : "Verbunden · synchronisiert …" : "Verbindung wird hergestellt …"}</small>{remainingSeconds !== null && remainingSeconds <= 5 * 60 && <small className="session-expiry-warning">Diese Sitzung endet automatisch in {remainingText}.</small>}</div>
        {realtime.session.participantRole === "FACILITATOR" && <button className="secondary small" disabled={!whiteboard.ready || whiteboard.busy || whiteboard.snapshot?.active} onClick={() => { void openWhiteboard(null); }}>Whiteboard öffnen</button>}
        <button className="secondary small" onClick={() => whiteboard.requestEnd(() => realtime.end())}>{realtime.session.participantRole === "FACILITATOR" ? "Sync beenden" : "Sync verlassen"}</button>
        {whiteboard.notice && <small role="status">{whiteboard.notice}</small>}
        {realtime.notice && <small className="session-notice">{realtime.notice}</small>}
        {message && <small>{message}</small>}
        {openRuns?.sessionId === realtime.session.id && realtime.session.participantRole === "FACILITATOR" && !whiteboard.snapshot?.active && createPortal(<ConfirmationModal title="Offene Übungsläufe beenden?" message={`${openRuns.count === 1 ? "Es gibt noch einen offenen Übungslauf" : `Es gibt noch ${openRuns.count} offene Übungsläufe`}, auch aus früheren Sitzungen. Sollen diese abgebrochen und das Whiteboard geöffnet werden? Gespeicherte Antworten und Ergebnisse bleiben erhalten. Die Läufe zählen nicht als erfolgreich abgeschlossen.`} onCancel={() => setOpenRuns(null)} onConfirm={() => { void openWhiteboard(); }} />, document.body)}
      </section>
    );
  }

  return (
    <section className="session-panel">
      <strong>Sitzung sync</strong>
      {canLead && <button className="secondary small" onClick={() => solo.transition(() => realtime.start()).catch((error) => setMessage(error.message))}>Sitzung starten</button>}
      <div className="session-join"><input aria-label="Sitzungscode" placeholder="Code" maxLength={12} value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} /><button className="secondary small" onClick={() => solo.transition(() => realtime.join(code)).catch((error) => setMessage(error.message))}>Beitreten</button></div>
      {realtime.notice && <small className="session-notice">{realtime.notice}</small>}
      {message && <small>{message}</small>}
    </section>
  );
}
